import { config } from '../../../../constants'
import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import MainModule from '../../../../modules/main'
import { isMobile } from '../../../../modules/utils'
import {
  getContentTypeFromUrl,
  getSlotContent,
  isEventPageType,
} from '../../../../utils/gtm/utilities'

export interface LvcSearchResultCard {
  href: string
  dataComponentLocation?: string | null
  isHovered: boolean
}
export class LvcSearchResultCard extends MinzeElement {
  attrs: Attrs = [['href', '#'], 'data-component-location']
  reactive: Reactive = [['isHovered', false]]

  onClick = async (event: Event) => {
    event.preventDefault()

    const clickedElement = event.target as HTMLElement
    const interactiveElementsToIgnore = [
      'LVC-FAVORITE-ICON-BUTTON',
      'LVC-EVENT-TICKET',
      'LVC-TAGS',
      'LVC-EVENT-SHOW-MORE',
      'LVC-GOOGLE-MAPS',
    ]

    if (!interactiveElementsToIgnore.includes(clickedElement.tagName)) {
      const currentPageUrl = window.location.href

      // Only track GTM analytics event if the destination link is an event page type.
      try {
        if (isEventPageType(this.href)) {
          const eventTitleText = getSlotContent(this, 'title') || 'Unknown Event'
          const destinationUrl = new URL(this.href, window.location.origin).toString()
          const sourcePageContentType = getContentTypeFromUrl(currentPageUrl)
          let analyticsEventName = 'vers_page_evenement'
          // Handle cases for the search results component within similarEvents component, eventSeries, artist, places and organizations pages.
          if (
            this.dataComponentLocation === 'evenements_similaires' ||
            sourcePageContentType === 'series' ||
            sourcePageContentType === 'artiste' ||
            sourcePageContentType === 'lieu' ||
            sourcePageContentType === 'organisation' ||
            sourcePageContentType === 'blogue' ||
            sourcePageContentType === 'destination'
          ) {
            analyticsEventName = 'vers_representation_deuxieme_niveau'
          }
          await MainModule.getInstance().analyticsTrack(analyticsEventName, {
            link_url: destinationUrl,
            link_text: eventTitleText,
            emplacement: sourcePageContentType,
            emplacement_url: currentPageUrl,
          })
        }
      } catch (analyticsError) {
        console.error('Error tracking event click:', analyticsError)
      }
      window.location.href = this.href
    }
  }
  handleMouseOver = () => {
    if (isMobile()) return
    this.isHovered = true
  }
  handleMouseLeave = () => {
    if (isMobile()) return
    this.isHovered = false
  }
  html = () => `
    <a href="${this.href}" class="results-card__card">
      <slot name="festival"></slot>
      <div class="results-card__wrapper">
        <slot name="title"></slot>
        <div class="results-card__info-wrapper">
          <slot name="date"></slot>
          <slot name="location"></slot>  
        </div>
        <slot name="tag"></slot>
        <slot name="more"></slot>
        <slot name="recommendations"></slot>
      </div>
      <div class="results-card__button-wrapper">
        <slot name="ticket"></slot>
        <slot name="favorite"></slot>
      </div>
      <slot name="image"></slot>
    </a>
  `
  css = () => `
    :host {
      width: 100%;
      padding: 30px;
      border-bottom: 1px solid rgba(30,0,125, 0.3);
      position: relative;
      transition: background 0.2s ease-in;
      background-image:linear-gradient(89.54deg, #BFF0FF 0%, #68CFEE 100%);
      background-position: 100% 0;
      background-size: 0 100%;
      background-repeat: no-repeat;

    }
    :host(:hover) {
      background-size: 100% 100%;
    }
    .results-card__card {
      text-decoration: none;
      color: #1E007D;
      width: 100%;
      display: flex;
    }
    .results-card__wrapper {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }
    .results-card__info-wrapper {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
    }
    .results-card__button-wrapper {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }
    slot[name="festival"]::slotted(*) {
      position: absolute;
      top: 7px;
    }
    slot[name="title"]::slotted(*) {
      max-width: 400px;
      font-size: 22px;
      font-weight: 800;
      line-height: 1.1;
      text-transform: uppercase;
    }
    slot[name="image"]::slotted(*) {
      pointer-events: none;
      border-radius: 10px;
      width: 310px;
      height: auto;
      object-fit: cover;
      aspect-ratio: 16/9;
      opacity: ${this.isHovered ? '1' : '0'};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin-left: auto;
      margin-right: auto;
      z-index: 2;
      transform: translate(${this.isHovered ? '50px' : '50px'}, ${this.isHovered ? '-60px' : '-30px'}) scale(${this.isHovered ? '1.05' : '1'});
      transition: transform 0.2s ease, opacity 0.2s ease, filter 0.2s ease, max-height 0.3s ease;
      transition-delay: 0.2s;
      filter: drop-shadow(${this.isHovered ? '0px 0px 15px rgba(0, 0, 0, 0.30)' : 'none'});
    }
    slot[name="favorite"]::slotted(*) {
      width: 50px;
      height: 50px;
    }
    slot[name="ticket"]::slotted(*) {
      height: 50px;
    }
    @media screen and (max-width: 991px) {
      :host {
        background-image: none;
      }
      slot[name="image"]::slotted(*) {
        display: none;
      }
    }
    @media screen and (max-width: 768px) {
      .results-card__card {
        flex-direction: column;
        justify-content: flex-start;
        gap: 1rem;
      }
      .results-card__button-wrapper {
        margin-left: 0;
        flex-direction: row-reverse;
        justify-content: flex-end;
      }
      .results-card__wrapper {
        gap: 1rem;
      }
    }
    @media screen and (max-width: 480px) {
      :host {
        padding: 2rem 1.5rem;
      }
      slot[name="title"]::slotted(*) {
        font-size: 18px;
      }
      .results-card__info-wrapper {
        flex-direction: column;
        gap: 4px;
        align-items: flex-start;
      }
    }
 
  `
  eventListeners: EventListeners = [
    ['a', 'click', this.onClick],
    [this, 'mouseover', this.handleMouseOver],
    [this, 'mouseleave', this.handleMouseLeave],
  ]
}
